import type { VentureId } from '@squadlab/contracts';
import type { EmployeeRepository } from '../ports/employee-repository.js';
import { parseEmployees } from '../core/employee-validation.js';
import { err, ok } from '../core/result.js';

/**
 * Mock EmployeeRepository — the v1 simulation's registry (ADR 0004, mock-always).
 *
 * Seeds two ventures so the "OS installed into each company" story is visible:
 * SquadLab core plus a demo subsidiary. Every read goes through boundary
 * parsing (persisted-on-read discipline) even though the data is local — the
 * seam is real, only the storage is fake. All people are fictional.
 */

export interface VentureInfo {
  readonly id: VentureId;
  readonly nameEn: string;
  readonly nameAr: string;
}

export const VENTURES: readonly VentureInfo[] = [
  { id: 'squadlab', nameEn: 'SquadLab Core', nameAr: 'سكواد لاب' },
  { id: 'falak', nameEn: 'Falak (demo venture)', nameAr: 'فلك (شركة تجريبية)' },
];

/** Raw, untyped seed rows — deliberately `unknown`-shaped so the parse seam is honest. */
const SEED_ROWS: readonly unknown[] = [
  {
    id: 'emp-sq-001',
    ventureId: 'squadlab',
    nameEn: 'Salman Al-Harbi',
    nameAr: 'سلمان الحربي',
    email: 'salman@squadlab.example',
    jobTitle: 'Studio Operations Lead',
    nationalId: '1084526910',
    gosiNumber: 'GOSI-501284416',
    saudiNational: true,
    contract: { type: 'indefinite', startDate: '2024-09-01' },
    salary: { basic: 1_800_000, housing: 450_000, transport: 120_000, other: 0 },
    documents: [{ kind: 'medical-insurance', reference: 'MED-88213', expiresOn: '2026-11-30' }],
  },
  {
    id: 'emp-sq-002',
    ventureId: 'squadlab',
    nameEn: 'Layla Al-Otaibi',
    nameAr: 'ليلى العتيبي',
    email: 'layla@squadlab.example',
    jobTitle: 'People & Culture Associate',
    nationalId: '1093310472',
    gosiNumber: 'GOSI-501287731',
    saudiNational: true,
    contract: {
      type: 'fixed-term',
      startDate: '2026-02-15',
      endDate: '2027-02-14',
      probationEndDate: '2026-05-15',
    },
    salary: { basic: 1_100_000, housing: 275_000, transport: 80_000, other: 0 },
    documents: [
      { kind: 'contract', reference: 'QIWA-C-77120', expiresOn: '2027-02-14' },
      { kind: 'medical-insurance', reference: 'MED-88907', expiresOn: '2026-12-31' },
    ],
  },
  {
    id: 'emp-sq-003',
    ventureId: 'squadlab',
    nameEn: 'Arjun Nair',
    nameAr: 'أرجون ناير',
    email: 'arjun@squadlab.example',
    jobTitle: 'Platform Engineer',
    nationalId: '2471083356',
    gosiNumber: 'GOSI-501290218',
    saudiNational: false,
    contract: { type: 'fixed-term', startDate: '2025-01-12', endDate: '2027-01-11' },
    salary: { basic: 1_500_000, housing: 375_000, transport: 100_000, other: 50_000 },
    documents: [
      // Iqama expiring soon on purpose — the P6 compliance watchdog feeds on this.
      { kind: 'iqama', reference: 'IQ-2471083356', expiresOn: '2026-08-20' },
      { kind: 'passport', reference: 'P-N8815532', expiresOn: '2028-01-15' },
      { kind: 'medical-insurance', reference: 'MED-90341', expiresOn: '2026-10-15' },
    ],
  },
  {
    id: 'emp-fk-001',
    ventureId: 'falak',
    nameEn: 'Noura Al-Qahtani',
    nameAr: 'نورة القحطاني',
    email: 'noura@falak.example',
    jobTitle: 'Venture Lead',
    nationalId: '1120043381',
    gosiNumber: 'GOSI-611203307',
    saudiNational: true,
    contract: {
      type: 'indefinite',
      startDate: '2026-06-01',
      probationEndDate: '2026-08-30',
    },
    salary: { basic: 1_600_000, housing: 400_000, transport: 100_000, other: 0 },
    documents: [{ kind: 'medical-insurance', reference: 'MED-91555', expiresOn: '2027-05-31' }],
  },
  {
    id: 'emp-fk-002',
    ventureId: 'falak',
    nameEn: 'Omar Haddad',
    nameAr: 'عمر حداد',
    email: 'omar@falak.example',
    jobTitle: 'Operations Associate',
    nationalId: '2519974150',
    gosiNumber: 'GOSI-611205992',
    saudiNational: false,
    contract: { type: 'fixed-term', startDate: '2026-03-10', endDate: '2027-03-09' },
    salary: { basic: 700_000, housing: 175_000, transport: 60_000, other: 0 },
    documents: [
      { kind: 'iqama', reference: 'IQ-2519974150', expiresOn: '2027-03-10' },
      { kind: 'passport', reference: 'P-J4402189', expiresOn: '2029-06-01' },
    ],
  },
];

export function createMockEmployeeRepository(): EmployeeRepository {
  return {
    async list(ventureId, signal) {
      if (signal.aborted) return err('cancelled', 'Employee list was cancelled');
      const parsed = parseEmployees(SEED_ROWS);
      if (!parsed.ok) return parsed;
      return ok(parsed.value.filter((e) => e.ventureId === ventureId));
    },

    async get(ventureId, employeeId, signal) {
      if (signal.aborted) return err('cancelled', 'Employee read was cancelled');
      const parsed = parseEmployees(SEED_ROWS);
      if (!parsed.ok) return parsed;
      const hit = parsed.value.find((e) => e.ventureId === ventureId && e.id === employeeId);
      return hit ? ok(hit) : err('not_found', `No employee ${employeeId} in venture ${ventureId}`);
    },
  };
}
